import { Link } from 'react-router-dom';
import { Calendar, BookOpen, FileText, Search, Bell, Users, Clock, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Button from './Button';

// SVG Illustrations
const illustrations = {
  noData: (
    <svg className="w-32 h-32 empty-illustration" viewBox="0 0 200 200" fill="none">
      <circle cx="100" cy="100" r="80" fill="currentColor" className="text-gray-100 dark:text-gray-800" />
      <rect x="60" y="70" width="80" height="60" rx="8" fill="currentColor" className="text-gray-200 dark:text-gray-700" />
      <rect x="70" y="85" width="40" height="6" rx="3" fill="currentColor" className="text-gray-300 dark:text-gray-600" />
      <rect x="70" y="97" width="60" height="6" rx="3" fill="currentColor" className="text-gray-300 dark:text-gray-600" />
      <rect x="70" y="109" width="30" height="6" rx="3" fill="currentColor" className="text-gray-300 dark:text-gray-600" />
      <circle cx="145" cy="55" r="25" fill="currentColor" className="text-[#589DD5]/20" />
      <path d="M145 45v20M135 55h20" stroke="currentColor" strokeWidth="3" strokeLinecap="round" className="text-[#589DD5]" />
    </svg>
  ),
  noModules: (
    <svg className="w-32 h-32 empty-illustration" viewBox="0 0 200 200" fill="none">
      <circle cx="100" cy="100" r="80" fill="currentColor" className="text-gray-100 dark:text-gray-800" />
      <rect x="50" y="60" width="100" height="80" rx="8" fill="currentColor" className="text-gray-200 dark:text-gray-700" />
      <rect x="60" y="75" width="30" height="30" rx="4" fill="currentColor" className="text-[#589DD5]/30" />
      <rect x="100" y="75" width="40" height="6" rx="3" fill="currentColor" className="text-gray-300 dark:text-gray-600" />
      <rect x="100" y="87" width="30" height="6" rx="3" fill="currentColor" className="text-gray-300 dark:text-gray-600" />
      <rect x="60" y="115" width="80" height="6" rx="3" fill="currentColor" className="text-gray-300 dark:text-gray-600" />
      <circle cx="75" cy="90" r="8" fill="currentColor" className="text-[#589DD5]" />
      <path d="M72 90l3 3 6-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  noCourses: (
    <svg className="w-32 h-32 empty-illustration" viewBox="0 0 200 200" fill="none">
      <circle cx="100" cy="100" r="80" fill="currentColor" className="text-gray-100 dark:text-gray-800" />
      <path d="M60 140V80a10 10 0 0110-10h60a10 10 0 0110 10v60a10 10 0 01-10 10H70a10 10 0 01-10-10z" fill="currentColor" className="text-gray-200 dark:text-gray-700" />
      <path d="M75 85h50M75 100h40M75 115h30" stroke="currentColor" strokeWidth="4" strokeLinecap="round" className="text-gray-300 dark:text-gray-600" />
      <circle cx="145" cy="145" r="20" fill="currentColor" className="text-[#589DD5]" />
      <path d="M145 138v14M138 145h14" stroke="white" strokeWidth="3" strokeLinecap="round" />
    </svg>
  ),
  noResults: (
    <svg className="w-32 h-32 empty-illustration" viewBox="0 0 200 200" fill="none">
      <circle cx="100" cy="100" r="80" fill="currentColor" className="text-gray-100 dark:text-gray-800" />
      <circle cx="90" cy="90" r="35" stroke="currentColor" strokeWidth="6" fill="none" className="text-gray-300 dark:text-gray-600" />
      <path d="M115 115l25 25" stroke="currentColor" strokeWidth="6" strokeLinecap="round" className="text-gray-300 dark:text-gray-600" />
      <path d="M80 80l20 20M100 80L80 100" stroke="currentColor" strokeWidth="4" strokeLinecap="round" className="text-[#589DD5]" />
    </svg>
  ),
  success: (
    <svg className="w-32 h-32 empty-illustration" viewBox="0 0 200 200" fill="none">
      <circle cx="100" cy="100" r="80" fill="currentColor" className="text-green-100 dark:text-green-900/30" />
      <circle cx="100" cy="100" r="50" fill="currentColor" className="text-green-500" />
      <path d="M75 100l15 15 35-35" stroke="white" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
};

export default function EmptyState({
  message,
  title,
  description,
  icon,
  illustration = 'noData',
  action,
  actionLabel,
  actionHref,
  className = '',
}) {
  const { t } = useTranslation();
  // Support legacy icon prop
  const IconComponent = icon;
  const showIllustration = !icon && illustrations[illustration];

  return (
    <div className={`flex flex-col items-center justify-center p-6 sm:p-10 text-center animate-fade-in ${className}`}>
      {/* Illustration or Icon */}
      {showIllustration ? (
        <div className="mb-4 sm:mb-6">
          {illustrations[illustration]}
        </div>
      ) : IconComponent ? (
        <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4 sm:mb-6">
          {typeof IconComponent === 'string' ? (
            <span className="text-3xl sm:text-4xl">{IconComponent}</span>
          ) : (
            <IconComponent className="w-8 h-8 sm:w-10 sm:h-10 text-gray-400" />
          )}
        </div>
      ) : null}

      {/* Title */}
      {title && (
        <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-2">
          {title}
        </h3>
      )}

      {/* Message/Description */}
      <p className="text-gray-500 dark:text-gray-400 text-sm sm:text-base max-w-md">
        {description || message}
      </p>

      {/* Action Button */}
      {(action || actionHref) && (
        <div className="mt-6">
          {actionHref ? (
            <Link to={actionHref}>
              <Button variant="primary">
                {actionLabel || t('actions.getStarted')}
              </Button>
            </Link>
          ) : (
            <Button variant="primary" onClick={action}>
              {actionLabel || t('actions.tryAgain')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// Preset empty states
export function NoModulesState() {
  const { t } = useTranslation();
  return (
    <EmptyState
      illustration="noModules"
      title={t('empty.noModulesTitle')}
      description={t('empty.noModulesDesc')}
    />
  );
}

export function NoCoursesState() {
  const { t } = useTranslation();
  return (
    <EmptyState
      illustration="noCourses"
      title={t('empty.noCoursesTitle')}
      description={t('empty.noCoursesDesc')}
      actionHref="/planner"
      actionLabel={t('empty.viewCourseSuggestions')}
    />
  );
}

export function NoResultsState({ query }) {
  const { t } = useTranslation();
  return (
    <EmptyState
      illustration="noResults"
      title={t('empty.noResults')}
      description={query ? t('empty.noResultsQuery', { query }) : t('empty.noResultsCriteria')}
    />
  );
}

export function SuccessState({ title, description }) {
  const { t } = useTranslation();
  return (
    <EmptyState
      illustration="success"
      title={title || t('empty.success')}
      description={description}
    />
  );
}
